/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Control, Controller } from "react-hook-form";
import {
  ExtendedTooltip,
  Label,
  MultipleSelect,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ExecutionPolicy } from "~/generated/graphql";
import { FormData } from "~/views/plans/PlanCreate/components/PlanCreateWizard/formConfig";
import { FormField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { ApprovalModeRadio } from "./ApprovalModeRadio";
import {
  HandleExecutionPolicyChange,
  HandleFieldChange,
} from "./hooks/usePlanSettingsFieldHandlers";
import { skipWindow } from "./options";
import { SelectButtonStyled } from "./SelectButtonStyled";
import { WindowDuration } from "./WindowDuration";

type SelectionOption<T extends string | boolean> = {
  description: string;
  disabled?: boolean;
  label: string;
  value: T;
};

type Props = {
  control: Control<FormData>;
  executionPolicies: SelectionOption<ExecutionPolicy>[];
  handleChange: HandleFieldChange;
  handleExecutionPolicyChange: HandleExecutionPolicyChange;
  helperTextColor?: string;
  resourceOperations: SelectionOption<boolean>[];
  showWindowSettings?: boolean;
  windowDurationLabel?: string;
};

export function PlanSettingsSections({
  control,
  executionPolicies,
  handleChange,
  handleExecutionPolicyChange,
  helperTextColor,
  resourceOperations,
  showWindowSettings = true,
  windowDurationLabel = "Window Duration",
}: Props) {
  return (
    <>
      <Label name="Execution Policy" css={{ marginBottom: 0 }} />
      <Text
        mb={theme.spacing.spacing02}
        color={helperTextColor}
        size="sm"
        tag="div"
      >
        {
          "Define how the next action triggers based on the current action's status."
        }
      </Text>
      <Controller
        control={control}
        name={FormField.EXECUTION_POLICY}
        render={({ field: { onChange, value } }) => {
          return (
            <>
              <MultipleSelect size="small">
                {executionPolicies.map((policy) => (
                  <ExtendedTooltip
                    caption={policy.description}
                    key={policy.value}
                    position="start"
                  >
                    <SelectButtonStyled
                      name={policy.value}
                      value={policy.value}
                      labelText={policy.label}
                      isActive={value === policy.value}
                      disabled={policy.disabled}
                      onClick={() =>
                        handleExecutionPolicyChange(onChange, policy.value)
                      }
                    />
                  </ExtendedTooltip>
                ))}
              </MultipleSelect>
              {value === ExecutionPolicy.Approval && (
                <>
                  <Spacer height={theme.spacing.spacing04} />
                  <Controller
                    control={control}
                    name={FormField.APPROVAL_MODE}
                    render={({
                      field: {
                        onChange: onApprovalModeChange,
                        value: approvalModeValue,
                      },
                    }) => (
                      <ApprovalModeRadio
                        value={approvalModeValue}
                        onChange={(nextValue) =>
                          handleChange(
                            FormField.APPROVAL_MODE,
                            onApprovalModeChange,
                            nextValue
                          )
                        }
                      />
                    )}
                  />
                </>
              )}
            </>
          );
        }}
      />
      <Spacer height={theme.spacing.spacing04} />
      <Label name="Resource Operation" css={{ marginBottom: 0 }} />
      <Text
        mb={theme.spacing.spacing02}
        color={helperTextColor}
        size="sm"
        tag="div"
      >
        Select the resource process below.
      </Text>
      <Controller
        control={control}
        name={FormField.RUN_IN_SEQUENCE}
        render={({ field: { onChange, value } }) => {
          return (
            <MultipleSelect size="small">
              {resourceOperations.map((operation) => (
                <ExtendedTooltip
                  caption={operation.description}
                  position="start"
                  key={operation.label}
                >
                  <SelectButtonStyled
                    key={operation.label}
                    name={operation.label}
                    value={operation.label}
                    labelText={operation.label}
                    isActive={value === operation.value}
                    disabled={operation.disabled}
                    onClick={() =>
                      handleChange(
                        FormField.RUN_IN_SEQUENCE,
                        onChange,
                        operation.value
                      )
                    }
                  />
                </ExtendedTooltip>
              ))}
            </MultipleSelect>
          );
        }}
      />
      {showWindowSettings && (
        <>
          <Spacer height={theme.spacing.spacing04} />
          <Label
            css={{ marginBottom: "0" }}
            htmlFor="activeWindowDuration"
            name={windowDurationLabel}
          />
          <Text
            mb={theme.spacing.spacing02}
            color={helperTextColor}
            size="sm"
            tag="div"
          >
            Enter the amount of time for each action. Any resources that are not
            up to date during this time period will not be executed.
          </Text>
          <WindowDuration control={control} handleChange={handleChange} />
          <Label name="Skip window" />
          <Controller
            control={control}
            name={FormField.SKIP_WINDOW}
            render={({ field: { onChange, value } }) => {
              return (
                <MultipleSelect size="small">
                  {skipWindow.map((skipWindowOption) => (
                    <ExtendedTooltip
                      caption={skipWindowOption.description}
                      position="start"
                      key={skipWindowOption.value}
                    >
                      <SelectButtonStyled
                        key={skipWindowOption.value}
                        name={skipWindowOption.value}
                        value={skipWindowOption.value}
                        labelText={skipWindowOption.label}
                        isActive={value === skipWindowOption.value}
                        onClick={() =>
                          handleChange(
                            FormField.SKIP_WINDOW,
                            onChange,
                            skipWindowOption.value
                          )
                        }
                      />
                    </ExtendedTooltip>
                  ))}
                </MultipleSelect>
              );
            }}
          />
        </>
      )}
    </>
  );
}
